import { Component, Input, Output, OnInit, OnChanges, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { AppService } from '../../app.service';
declare let d3;
declare const SiDataViz;

@Component({
  selector: 'si-outer-axis',
  templateUrl: './outer-axis.component.html',
  styleUrls: ['./outer-axis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OuterAxisComponent implements OnInit, OnChanges {
  public isPlotted: boolean = false;
  public computedWidth: number;
  public computedHeight: number;
  public axisContainer;
  public axisContainerSVG;
  public axisContainerSVGGroup;
  public entries: Array<object>;
  public xScale;

  public xLabelContainerSVG;
  public xLabelContainerSVGGroup;
  public xLabelText;

  public yLabelContainer;
  public yLabelText;

  @Input() xAxisLabel: object = { isVisible: true, value: 'x-axis' };
  @Input() yAxisLabel: object = { isVisible: true, value: 'y-axis', position: { left: -30, top: '' } };
  @Input() id: string;
  @Input() data: Array<object>;
  @Input() width: number;
  @Input() height: number;
  @Input() margin: object;
  @Input() xScaleType: object = { type: 'linear' };
  @Input() xAxisCenter: boolean = false;
  @Input() x: string;
  @Input() y: string;
  @Input() xTickFormat = function (d) { return d; };

  constructor(private appService: AppService) { }

  ngOnInit() {

  }

  ngOnChanges() {
    if (this.data !== undefined) {
      if (!this.data.length && this.isPlotted) {
        this.axisContainer.select('svg').remove();
        if (this.yAxisLabel['isVisible']) {
          this.yLabelContainer.text('');
        }
        this.isPlotted = false;
        return;
      }
      if (this.data.length && !this.isPlotted) {
        this.createAxisContainer();
        this.createAxis();
        if (this.xAxisLabel['isVisible']) {
          this.createXAxisLabelContainer();
        }
        this.applyWindowResize();
        this.isPlotted = true;
      }
      this.updateContainer();
      this.prepareData();
      this.defineScales();
      this.updateAxis();
      this.adjustXAxisTicks();

      // It works only with initial value of 'isVisible' whether true or false. Any update in value will not work as of now.
      if (this.xAxisLabel['isVisible']) {
        this.addXAxisLabel();
      }
      if (this.yAxisLabel['isVisible'] && this.data !== undefined && this.data.length) {
        this.addYAxisLabel();
      }

    }
  }

  createXAxisLabelContainer() {
    this.xLabelContainerSVGGroup = this.axisContainerSVG.append('g')
      .attr('class', 'si-outer-x-axis-label-group');

    this.xLabelText = this.xLabelContainerSVGGroup
      .append('text')
      .attr('class', 'si-x-axis-label')
      .attr('text-anchor', 'middle');
  }

  addXAxisLabel() {
    this.xLabelContainerSVGGroup.attr('transform', 'translate(' + (this.margin['left'] + this.computedWidth / 2) + ', 35)');

    this.xLabelText
      .text(() => {
        return this.xAxisLabel['value'];
      });
  }

  createAxisContainer() {
    this.axisContainer = d3.select('#' + this.id + ' #xAxisContainerOuter');
    this.axisContainerSVG = this.axisContainer
      .append('svg');

    this.axisContainerSVGGroup = this.axisContainerSVG.append('g')
      .attr('class', 'si-outer-x-axis-group');
  }


  addYAxisLabel() {
    const height = this.axisContainer.node().parentNode.parentNode.getBoundingClientRect().height;

    this.yLabelContainer = d3.select('#' + this.id + ' #yAxisLabelContainerOuter')
      .attr('class', 'si-outer-y-axis-label')
      .style('position', 'absolute')
      .style('top', () => {
        return (this.yAxisLabel['position']['top'] || height / 2) + 'px';
      })
      .style('left', () => {
        return (this.yAxisLabel['position']['left'] || -30) + 'px';
      })
      .text(this.yAxisLabel['value'])
      .style('transform', 'rotate(-90deg)');
  }

  updateContainer() {
    const width = this.width || this.axisContainer.node().getBoundingClientRect().width ||
      this.axisContainer.node().parentNode.getBoundingClientRect().width;

    this.computedWidth = width - this.margin['left'] - this.margin['right'];
    this.computedHeight = this.height || this.axisContainer.node().getBoundingClientRect().height ||
      this.axisContainer.node().parentNode.getBoundingClientRect().height;

    this.axisContainerSVG
      .attr('width', width)
      .attr('height', this.computedHeight);

    this.axisContainerSVGGroup.attr('transform', 'translate(' + this.margin['left'] + ', 0)');
  }

  createAxis() {
    this.axisContainerSVGGroup.append('g')
      .attr('class', 'si-outer-x-axis');
  }

  prepareData() {
    const max = -1;
    const self = this;
    this.entries = d3.nest()
      .key((d) => d[this.y])
      .rollup((leaves) => {

        // Transform data for -ve value support
        const LeavesArray = [];
        const last = {
          X0: SiDataViz.helper.getAxisFirstTick(this.xScaleType),
          X1: SiDataViz.helper.getAxisFirstTick(this.xScaleType),
          X2: SiDataViz.helper.getAxisFirstTick(this.xScaleType),
          X3: SiDataViz.helper.getAxisFirstTick(this.xScaleType)
        };
        leaves.forEach((leaf, i) => {

          const tempObj = leaf;
          tempObj.X0 = SiDataViz.helper.getAxisFirstTick(this.xScaleType);

          if (!LeavesArray[i - 1]) {
            if (leaf[this.x] >= 0) {
              tempObj.X0 = SiDataViz.helper.getAxisFirstTick(this.xScaleType);
              tempObj.X1 = leaf[this.x];

              last.X0 = SiDataViz.helper.getAxisFirstTick(this.xScaleType);
              last.X1 = leaf[this.x];
            } else {
              tempObj.X2 = SiDataViz.helper.getAxisFirstTick(this.xScaleType);
              tempObj.X3 = leaf[this.x];

              last.X2 = SiDataViz.helper.getAxisFirstTick(this.xScaleType);
              last.X3 = leaf[this.x];
            }
          } else {
            if (leaf[this.x] >= 0) {
              tempObj.X0 = last.X0 + last.X1;
              tempObj.X1 = leaf[this.x];

              last.X0 = last.X0 + last.X1;
              last.X1 = leaf[this.x];
            } else {
              tempObj.X2 = last.X2 + last.X3;
              tempObj.X3 = leaf[this.x];

              last.X2 = last.X2 + last.X3;
              last.X3 = leaf[this.x];
            }
          }
          LeavesArray.push(tempObj);
        });
        return LeavesArray;
      })
      .entries(this.data);
    this.entries.forEach(function (entry: object, i: number) {
      entry['total'] = d3.sum(entry['value'], function (d) { return d.X1; });
      entry['totalValueMin'] = d3.sum(entry['value'], function (d) { return d.X3; });
    });

  }

  defineScales() {
    let xAxisDomain;
    const xScaleRange = [0, this.computedWidth];

    let xScaleMaxValue = d3.max(this.entries, function (d) { return d.total; });
    let xScaleMinValue = d3.min(this.entries, function (d) { return d.totalValueMin; });
    if (xScaleMinValue < 0 && this.xAxisCenter) {
      // If minimum value is Negative - then compare mod(min -ve value) with max +ve value
      // whichever is greater, set that as min & max for scale
      if (Math.abs(xScaleMinValue) > Math.abs(xScaleMaxValue)) {
        xScaleMaxValue = Math.abs(xScaleMinValue);
      } else {
        xScaleMinValue = Math.abs(xScaleMaxValue) * (-1);
      }
    }

    // Adding a buffer of 5% on max & min of Y-Scale
    // so that a padding/gap is added from top and bottom on max value bars
    xScaleMaxValue += 0.05 * xScaleMaxValue;
    xScaleMinValue += 0.05 * Math.abs(xScaleMinValue) * (-1);

    xAxisDomain = [xScaleMinValue, xScaleMaxValue];

    this.xScale = SiDataViz.helper.setScale.apply(this, [this.xScaleType, xScaleRange, xAxisDomain]);
  }

  updateAxis() {
    const xAxisFunction = d3.axisBottom(this.xScale).tickFormat(d3.format(this.xTickFormat));
    // update x Axis
    this.axisContainerSVGGroup.select('.si-outer-x-axis').call(xAxisFunction);
  }

  adjustXAxisTicks() {
    const self = this;
    let tickStartX = 0,
      tickEndX = 0,
      tickBox,
      transform,
      transformX;

    const tickSpaceBuffer = 1,
      selectedTicks = this.axisContainerSVGGroup.select('.si-outer-x-axis').selectAll('.tick');

    selectedTicks.attr('opacity', function (d, i: number) {
      d3.select(this).select('text').text(self.appService.replaceFormatter(d, self.xTickFormat));
      tickBox = this.getBBox();
      transform = d3.select(this).attr('transform');
      transformX = transform.split(',')[0].split('(')[1];
      tickStartX = Number(transformX) - Number(tickBox.width / 2) - tickSpaceBuffer;
      /*if (tickEndX == 0) {
        if (tickStartX < 0) {
          return 0;
        }
      } else {*/
      if (i > 0 && tickStartX < tickEndX) {
        return 0;
      }
      // }
      tickEndX = Number(transformX) + Number((tickBox.width / 2));
    });
  }

  applyWindowResize() {
    let resizeTaskId = null;

    window.addEventListener('resize', evt => {
      if (resizeTaskId !== null) {
        clearTimeout(resizeTaskId);
      }

      resizeTaskId = setTimeout(() => {
        resizeTaskId = null;
        this.updateContainer();
        this.prepareData();
        this.defineScales();
        this.updateAxis();
        this.adjustXAxisTicks();
        this.addXAxisLabel();
        this.addYAxisLabel();
      }, 100);
    });
  }
}

